import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthCallback() {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session ID found');
        }

        // Exchange session_id for user data
        const user = await processGoogleSession(sessionId);
        
        toast.success(`Welcome, ${user.name}!`);
        
        // Redirect based on role
        const dest = user.role === 'admin' ? '/admin'
          : user.role === 'counselor' ? '/counselor'
          : '/dashboard';
        
        // Clean URL and navigate
        window.history.replaceState({}, document.title, dest);
        navigate(dest, { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        const detail = error?.response?.data?.detail || 'Authentication failed';
        if (error?.response?.status === 403) {
          toast.error(detail, { duration: 8000 });
          window.history.replaceState({}, document.title, '/');
          navigate('/', { replace: true });
        } else {
          toast.error(detail);
          navigate('/login', { replace: true });
        }
      }
    };

    processSession();
  }, [processGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
          <Brain className="h-10 w-10 text-primary animate-pulse" strokeWidth={1.5} />
        </div>
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-muted-foreground font-mono text-sm">Initializing MindFlō...</p>
        </div>
      </div>
    </div>
  );
}
